import React, { useState } from "react";
import Select from "react-select"; // React Select for multi-select dropdown
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";

const CreateTreatmentPlan = ({ patient, proceduresMaster, onSavePlan }) => {
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [customPlan, setCustomPlan] = useState("");
  const [treatment_note, setTreatment_note] = useState({
    reason: "",
    resources: "",
  });
  const [startDelay, setStartDelay] = useState(0);
  const [treatmentDuration, setTreatmentDuration] = useState("");

  // Compute start and end dates based on today's date, the start delay and duration
  const today = new Date();
  const computedStartDate = new Date(today);
  computedStartDate.setDate(today.getDate() + Number(startDelay));

  const computedEndDate = new Date(computedStartDate);
  computedEndDate.setDate(
    computedStartDate.getDate() + Number(treatmentDuration)
  );

  // Format the dates for display as "01 March 2025"
  const formattedStartDateDisplay = computedStartDate.toLocaleDateString(
    "en-US",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
  const formattedEndDateDisplay = computedEndDate.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // ISO format for saving
  const formattedStartDateSave = computedStartDate.toISOString().slice(0, 10);
  const formattedEndDateSave = computedEndDate.toISOString().slice(0, 10);

  const handleSavePlan = () => {
    const treatmentPlan = {
      patientId: patient.id,
      procedures: selectedProcedures?.map((opt) => opt.value), // Extract procedure details
      customPlan,
      treatment_note,
      startDate: formattedStartDateSave,
      endDate: formattedEndDateSave,
    };
    onSavePlan(treatmentPlan);
  };

  // Placeholder functions for edit and view actions
  const handleEditProcedure = (procedure) => {
    console.log("Edit procedure:", procedure);
    // Replace with your actual edit logic
  };

  const handleViewProcedure = (procedure) => {
    console.log("View procedure:", procedure);
    // Replace with your actual view logic
  };

  // Function to delete a procedure from the selected list
  const handleDeleteProcedure = (procedure) => {
    setSelectedProcedures(
      selectedProcedures.filter((opt) => opt.value.id !== procedure.id)
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-700">
          Treatment Plan for {patient.name}
        </h1>
        <p className="text-gray-600">Patient ID: {patient.id}</p>
      </div>

      {/* Multi-Select Dropdown for Procedures */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Select Procedures
        </h2>
        <Select
          options={proceduresMaster?.map((procedure) => ({
            value: procedure,
            label: `${procedure.procedureName} (₹${procedure.baseCost})`,
          }))}
          isMulti
          value={selectedProcedures}
          onChange={(selectedOptions) => setSelectedProcedures(selectedOptions)}
          placeholder="Select procedures"
          className="w-full"
          classNamePrefix="react-select"
        />
      </div>

      {/* Selected Procedures Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Selected Procedures
        </h2>
        {selectedProcedures.length > 0 ? (
          <ul className="space-y-4">
            {selectedProcedures?.map((opt, index) => (
              <li
                key={index}
                className="bg-white p-4 shadow rounded-lg flex justify-between items-center"
              >
                <div>
                  <h3 className="text-gray-700 font-semibold">
                    {opt.value.procedureName}
                  </h3>
                  <p className="text-sm text-gray-500">₹{opt.value.baseCost}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewProcedure(opt.value)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={() => handleEditProcedure(opt.value)}
                    className="text-yellow-500 hover:text-yellow-700"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteProcedure(opt.value)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-600">No procedures selected.</p>
        )}
      </div>

      {/* Custom Plan Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Custom Plan
        </h2>
        <textarea
          value={customPlan}
          onChange={(e) => setCustomPlan(e.target.value)}
          placeholder="Describe custom treatment plan"
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-gray-200"
          rows="4"
        />
      </div>

      {/* Treatment Timing Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Treatment Timing
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Start Delay Input */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Treatment will start in (days)
            </label>
            <input
              type="number"
              value={startDelay}
              onChange={(e) => setStartDelay(e.target.value)}
              className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1 text-sm text-gray-500">
              Scheduled Start Date: {formattedStartDateDisplay}
            </p>
          </div>
          {/* Treatment Duration Input */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Treatment Duration (days)
            </label>
            <input
              type="number"
              value={treatmentDuration}
              onChange={(e) => setTreatmentDuration(e.target.value)}
              className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1 text-sm text-gray-500">
              Scheduled End Date: {formattedEndDateDisplay}
            </p>
          </div>
        </div>
      </div>

      {/* Treatment Note Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Treatment Note
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">
              Reason for Treatment
            </label>
            <textarea
              value={treatment_note.reason}
              onChange={(e) =>
                setTreatment_note({ ...treatment_note, reason: e.target.value })
              }
              placeholder="Enter reason for treatment"
              className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-gray-200"
            />
          </div>
        </div>
      </div>

      {/* Save Plan Button */}
      <button
        onClick={handleSavePlan}
        className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition"
      >
        Save Treatment Plan
      </button>
    </div>
  );
};

export default CreateTreatmentPlan;
